import { Head, Link, useForm } from '@inertiajs/react';
import AppLayout from '@/Layouts/AppLayout';
import Card, { CardBody } from '@/Components/Card';
import Button from '@/Components/Button';
import FormInput from '@/Components/FormInput';
import FormTextarea from '@/Components/FormTextarea';
import PageHeader from '@/Components/PageHeader';
import Alert from '@/Components/Alert';
import {
    Cog6ToothIcon,
    BuildingOfficeIcon,
    UsersIcon,
    ShieldCheckIcon,
    UserCircleIcon,
    QrCodeIcon,
    EnvelopeIcon,
    ClipboardDocumentCheckIcon,
    DocumentTextIcon,
    ChartBarIcon,
    KeyIcon,
    PhotoIcon,
    GlobeAltIcon,
    CreditCardIcon,
    ArrowTopRightOnSquareIcon,
    ChevronRightIcon,
} from '@heroicons/react/24/outline';

const quickLinks = [
    { label: 'Users', desc: 'Manage team members & access', icon: UsersIcon, href: '/users', color: 'bg-blue-50 text-blue-600' },
    { label: 'Roles & Permissions', desc: 'Define roles and access control', icon: ShieldCheckIcon, href: '/roles', color: 'bg-purple-50 text-purple-600' },
    { label: 'Profile', desc: 'Your personal account settings', icon: UserCircleIcon, href: '/profile', color: 'bg-emerald-50 text-emerald-600' },
    { label: 'Suppliers', desc: 'Manage supplier directory', icon: BuildingOfficeIcon, href: '/suppliers', color: 'bg-orange-50 text-orange-600' },
    { label: 'Customers', desc: 'Manage customer directory', icon: EnvelopeIcon, href: '/customers', color: 'bg-teal-50 text-teal-600' },
    { label: 'QR Codes', desc: 'Registration QR code setup', icon: QrCodeIcon, href: '/qr-codes', color: 'bg-indigo-50 text-indigo-600' },
    { label: 'Materials', desc: 'Material requisition management', icon: ClipboardDocumentCheckIcon, href: '/materials', color: 'bg-rose-50 text-rose-600' },
    { label: 'Daily Reports', desc: 'Daily operations tracking', icon: DocumentTextIcon, href: '/daily-reports', color: 'bg-amber-50 text-amber-600' },
    { label: 'Reports', desc: 'Analytics & generated reports', icon: ChartBarIcon, href: '/reports', color: 'bg-cyan-50 text-cyan-600' },
];

export default function Index({ settings, flash }) {
    const { data, setData, post, processing, errors } = useForm({
        _method: 'PUT',
        company_name: settings?.company_name || '',
        email: settings?.email || '',
        phone: settings?.phone || '',
        address: settings?.address || '',
        website: settings?.website || '',
        trade_license_no: settings?.trade_license_no || '',
        vat_number: settings?.vat_number || '',
        city: settings?.city || '',
        country: settings?.country || '',
        po_box: settings?.po_box || '',
        logo: null,
        remove_logo: false,
    });

    const submit = (e) => {
        e.preventDefault();
        post('/settings', { forceFormData: true });
    };

    const handleRemoveLogo = () => {
        setData('remove_logo', true);
        post('/settings', { forceFormData: true });
    };

    return (
        <AppLayout>
            <Head title="Settings" />
            <PageHeader title="Settings" subtitle="Manage your organization, team, and system configuration" />
            {flash?.success && <Alert type="success" className="mb-6">{flash.success}</Alert>}

            {/* ===== Quick Navigation ===== */}
            <div className="mb-8">
                <h2 className="text-sm font-bold text-slate-500 uppercase tracking-wider mb-4 flex items-center gap-2">
                    <ArrowTopRightOnSquareIcon className="w-4 h-4" /> Quick Navigation
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                    {quickLinks.map((item) => (
                        <Link
                            key={item.href}
                            href={item.href}
                            className="group flex items-center gap-4 p-4 bg-white rounded-2xl border border-slate-200/80 hover:border-emerald-300 hover:shadow-md hover:shadow-emerald-100/50 transition-all duration-200"
                        >
                            <div className={`w-11 h-11 rounded-xl ${item.color} flex items-center justify-center flex-shrink-0 transition-transform group-hover:scale-105`}>
                                <item.icon className="w-5 h-5" />
                            </div>
                            <div className="flex-1 min-w-0">
                                <p className="text-sm font-semibold text-slate-800 group-hover:text-emerald-700 transition-colors">{item.label}</p>
                                <p className="text-xs text-slate-400 truncate">{item.desc}</p>
                            </div>
                            <ChevronRightIcon className="w-4 h-4 text-slate-300 group-hover:text-emerald-400 transition-colors flex-shrink-0" />
                        </Link>
                    ))}
                </div>
            </div>

            {/* ===== Company Profile Form ===== */}
            <form onSubmit={submit}>
                <div className="mb-6">
                    <h2 className="text-sm font-bold text-slate-500 uppercase tracking-wider mb-4 flex items-center gap-2">
                        <BuildingOfficeIcon className="w-4 h-4" /> Company Profile
                    </h2>
                </div>

                <Card className="mb-6">
                    <div className="px-6 py-4 border-b border-slate-100 flex items-center gap-3">
                        <div className="w-8 h-8 rounded-lg bg-emerald-50 flex items-center justify-center">
                            <GlobeAltIcon className="w-4 h-4 text-emerald-600" />
                        </div>
                        <div>
                            <h3 className="font-semibold text-slate-800 text-sm">General Information</h3>
                            <p className="text-xs text-slate-400">Company name, contact, and registration details</p>
                        </div>
                    </div>
                    <CardBody className="space-y-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <FormInput label="Company Name" value={data.company_name} onChange={e => setData('company_name', e.target.value)} error={errors.company_name} required />
                            <FormInput label="Email" type="email" value={data.email} onChange={e => setData('email', e.target.value)} error={errors.email} />
                            <FormInput label="Phone" value={data.phone} onChange={e => setData('phone', e.target.value)} error={errors.phone} />
                            <FormInput label="Website" type="url" value={data.website} onChange={e => setData('website', e.target.value)} error={errors.website} />
                        </div>
                    </CardBody>
                </Card>

                <Card className="mb-6">
                    <div className="px-6 py-4 border-b border-slate-100 flex items-center gap-3">
                        <div className="w-8 h-8 rounded-lg bg-blue-50 flex items-center justify-center">
                            <CreditCardIcon className="w-4 h-4 text-blue-600" />
                        </div>
                        <div>
                            <h3 className="font-semibold text-slate-800 text-sm">Registration & Tax</h3>
                            <p className="text-xs text-slate-400">Trade license and VAT information</p>
                        </div>
                    </div>
                    <CardBody className="space-y-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <FormInput label="Trade License No." value={data.trade_license_no} onChange={e => setData('trade_license_no', e.target.value)} error={errors.trade_license_no} />
                            <FormInput label="VAT Number" value={data.vat_number} onChange={e => setData('vat_number', e.target.value)} error={errors.vat_number} />
                        </div>
                    </CardBody>
                </Card>

                <Card className="mb-6">
                    <div className="px-6 py-4 border-b border-slate-100 flex items-center gap-3">
                        <div className="w-8 h-8 rounded-lg bg-purple-50 flex items-center justify-center">
                            <BuildingOfficeIcon className="w-4 h-4 text-purple-600" />
                        </div>
                        <div>
                            <h3 className="font-semibold text-slate-800 text-sm">Location</h3>
                            <p className="text-xs text-slate-400">Physical address and mailing information</p>
                        </div>
                    </div>
                    <CardBody className="space-y-4">
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <FormInput label="City" value={data.city} onChange={e => setData('city', e.target.value)} error={errors.city} />
                            <FormInput label="Country" value={data.country} onChange={e => setData('country', e.target.value)} error={errors.country} />
                            <FormInput label="P.O. Box" value={data.po_box} onChange={e => setData('po_box', e.target.value)} error={errors.po_box} />
                        </div>
                        <FormTextarea label="Full Address" value={data.address} onChange={e => setData('address', e.target.value)} error={errors.address} rows={3} />
                    </CardBody>
                </Card>

                <Card className="mb-6">
                    <div className="px-6 py-4 border-b border-slate-100 flex items-center gap-3">
                        <div className="w-8 h-8 rounded-lg bg-amber-50 flex items-center justify-center">
                            <PhotoIcon className="w-4 h-4 text-amber-600" />
                        </div>
                        <div>
                            <h3 className="font-semibold text-slate-800 text-sm">Company Logo</h3>
                            <p className="text-xs text-slate-400">Used in reports, PDFs, and system branding</p>
                        </div>
                    </div>
                    <CardBody>
                        <div className="flex items-center gap-6">
                            {settings?.logo_path && !data.remove_logo ? (
                                <div className="relative group">
                                    <img src={`/storage/${settings.logo_path}`} alt="Logo" className="w-20 h-20 rounded-xl border border-slate-200 object-contain bg-white shadow-sm" />
                                    <button type="button" onClick={handleRemoveLogo} className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 hover:bg-red-600 text-white rounded-full flex items-center justify-center text-xs transition-colors shadow-sm opacity-0 group-hover:opacity-100">&times;</button>
                                </div>
                            ) : (
                                <div className="w-20 h-20 rounded-xl border-2 border-dashed border-slate-200 flex items-center justify-center bg-slate-50">
                                    <PhotoIcon className="w-8 h-8 text-slate-300" />
                                </div>
                            )}
                            <div>
                                <input type="file" accept="image/png,image/jpg,image/jpeg,image/svg+xml" onChange={e => setData('logo', e.target.files[0])} className="text-sm text-slate-500 file:mr-4 file:py-2 file:px-4 file:rounded-xl file:border-0 file:text-sm file:font-medium file:bg-emerald-50 file:text-emerald-700 hover:file:bg-emerald-100 file:cursor-pointer file:transition-colors" />
                                <p className="text-xs text-slate-400 mt-2">PNG, JPG, SVG up to 2MB. Recommended: 200x200px</p>
                                {errors.logo && <p className="text-xs text-red-500 mt-1">{errors.logo}</p>}
                            </div>
                        </div>
                    </CardBody>
                </Card>

                <div className="flex gap-3">
                    <Button type="submit" disabled={processing}>
                        {processing ? 'Saving...' : 'Save Settings'}
                    </Button>
                    <Button type="button" variant="outline" onClick={() => window.location.reload()}>Reset</Button>
                </div>
            </form>
        </AppLayout>
    );
}
